import { useState, useCallback, useMemo } from "react"
import { Copy, Check, Download, WrapText } from "lucide-react"
import { Button } from "@aix/ui"
import toast from "react-hot-toast"

interface AuraCodeViewProps {
  html: string
  onChange?: (html: string) => void
  readOnly?: boolean
}

const AuraCodeView = ({ html, onChange, readOnly = false }: AuraCodeViewProps) => {
  const [copied, setCopied] = useState(false)
  const [wordWrap, setWordWrap] = useState(true)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(html)
    setCopied(true)
    toast.success("Code copied to clipboard")
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDownload = () => {
    const blob = new Blob([html], { type: "text/html" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "index.html"
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success("HTML file downloaded")
  }

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(e.target.value)
      }
    },
    [onChange],
  )

  // Simple syntax highlighting (applied via CSS)
  const highlightedCode = useMemo(() => {
    return (
      html
        // HTML tags
        .replace(/(&lt;|<)(\/?[\w-]+)/g, '<span class="text-pink-500">$1$2</span>')
        // Attributes
        .replace(/(\s)([\w-]+)(=)/g, '$1<span class="text-yellow-500">$2</span>$3')
        // Strings
        .replace(/"([^"]*)"/g, '<span class="text-green-500">"$1"</span>')
        // Comments
        .replace(/(&lt;!--|<!--)([\s\S]*?)(--&gt;|-->)/g, '<span class="text-gray-500">$1$2$3</span>')
    )
  }, [html])

  const lines = html.split("\n")
  const lineCount = lines.length

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-background">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="font-mono">index.html</span>
          <span className="text-xs bg-secondary px-2 py-0.5 rounded">HTML</span>
          <span className="text-xs">{lineCount} lines</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setWordWrap(!wordWrap)}
            className={wordWrap ? "bg-secondary" : ""}
            title="Toggle word wrap"
          >
            <WrapText className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleCopy}>
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-1" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-1" />
                Copy
              </>
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleDownload}>
            <Download className="w-4 h-4 mr-1" />
            Download
          </Button>
        </div>
      </div>

      {/* Code Editor */}
      <div className="flex-1 overflow-auto bg-gray-950">
        <div className="flex min-h-full">
          {/* Line Numbers */}
          <div className="flex-shrink-0 py-4 pr-4 text-right select-none bg-gray-900/50 border-r border-gray-800">
            {lines.map((_, i) => (
              <div key={i} className="px-4 text-xs font-mono text-gray-500 leading-6">
                {i + 1}
              </div>
            ))}
          </div>

          {/* Code Content */}
          <div className="flex-1 relative">
            {readOnly ? (
              <pre
                className={`p-4 text-sm font-mono text-gray-300 leading-6 ${
                  wordWrap ? "whitespace-pre-wrap break-words" : "whitespace-pre"
                }`}
                dangerouslySetInnerHTML={{ __html: highlightedCode }}
              />
            ) : (
              <textarea
                value={html}
                onChange={handleChange}
                spellCheck={false}
                className={`w-full h-full min-h-full p-4 text-sm font-mono text-gray-300 bg-transparent leading-6 resize-none focus:outline-hidden ${
                  wordWrap ? "whitespace-pre-wrap break-words" : "whitespace-pre overflow-x-auto"
                }`}
                style={{ tabSize: 2 }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="flex items-center justify-between px-4 py-1 border-t border-border bg-secondary/30 text-xs text-muted-foreground">
        <div className="flex items-center gap-4">
          <span>HTML</span>
          <span>UTF-8</span>
        </div>
        <div className="flex items-center gap-4">
          <span>{html.length.toLocaleString()} characters</span>
          <span>Ln {lineCount}</span>
        </div>
      </div>
    </div>
  )
}

export default AuraCodeView
